import '@google/model-viewer'
import '../styles/fonts.css'
import { initDebugConsole, isUrlDebugEnabled } from './shared/debug-console.js'

initDebugConsole()

const ua = navigator.userAgent
const isIOS =
  /iP(hone|ad|od)/i.test(ua) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
const isAndroid = /Android/i.test(ua)
const isMobile = isIOS || isAndroid

/** Stop any preview getUserMedia streams so AR can use the camera. */
function releasePreviewCameras() {
  document.querySelectorAll('video').forEach((video) => {
    const stream = video.srcObject
    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop())
      video.srcObject = null
    }
  })
  document.dispatchEvent(new CustomEvent('quilt:release-camera'))
}

/** Let the browser paint "Starting…" before heavy USDZ work. */
function waitForPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

/**
 * After Quick Look / Scene Viewer launches, the page is usually hidden.
 * Stay in the loading state until the user dismisses AR and returns.
 */
function waitForReturnFromAR() {
  return new Promise((resolve) => {
    let leftPage = document.hidden
    let enterTimeout
    let safetyTimeout

    const finish = () => {
      cleanup()
      resolve()
    }

    const onVisibility = () => {
      if (document.hidden) {
        leftPage = true
      } else if (leftPage) {
        finish()
      }
    }

    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pageshow', onVisibility)
      window.removeEventListener('focus', onVisibility)
      clearTimeout(enterTimeout)
      clearTimeout(safetyTimeout)
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pageshow', onVisibility)
    window.addEventListener('focus', onVisibility)

    enterTimeout = setTimeout(() => { leftPage = true }, 3000)
    safetyTimeout = setTimeout(finish, 10 * 60 * 1000)
  })
}

/** Centered spinner while the GLB is loading or switching. */
function setupModelLoading(mv) {
  const style = document.createElement('style')
  style.textContent = `
    .mv-loader {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 15;
      pointer-events: none;
      opacity: 0;
      visibility: hidden;
      transition: opacity 140ms ease, visibility 140ms ease;
    }
    .mv-loader.is-visible {
      opacity: 1;
      visibility: visible;
    }
    .mv-loader__spinner {
      width: clamp(58px, 14vw, 76px);
      height: clamp(58px, 14vw, 76px);
      border-radius: 50%;
      border: 4px solid rgba(255, 255, 255, 0.3);
      border-top-color: #fff;
      animation: mv-loader-spin 0.9s linear infinite;
    }
    @keyframes mv-loader-spin {
      to { transform: rotate(360deg); }
    }
  `
  document.head.appendChild(style)

  const loader = document.createElement('div')
  loader.className = 'mv-loader'
  loader.setAttribute('role', 'status')
  loader.setAttribute('aria-label', 'Loading model')
  loader.innerHTML = '<div class="mv-loader__spinner"></div>'
  document.body.appendChild(loader)

  let visible = false
  let awaitingNewModel = false

  function show() {
    if (visible) return
    visible = true
    loader.classList.add('is-visible')
  }

  function hide() {
    if (!visible) return
    visible = false
    loader.classList.remove('is-visible')
  }

  function hideIfLoaded() {
    if (mv.loaded && !awaitingNewModel) hide()
  }

  mv.addEventListener('load', () => {
    awaitingNewModel = false
    hide()
  })
  mv.addEventListener('error', () => {
    awaitingNewModel = false
    hide()
  })
  mv.addEventListener('progress', (e) => {
    if (e.detail.totalProgress < 1 && (!mv.loaded || awaitingNewModel)) show()
  })

  new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.attributeName === 'src') {
        awaitingNewModel = true
        show()
      }
    }
  }).observe(mv, { attributes: true, attributeFilter: ['src'] })

  mv.addEventListener('quilt:model-loading', () => {
    awaitingNewModel = true
    show()
  })

  // Returning from AR can re-fire progress; hide if the model was already loaded.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) hideIfLoaded()
  })
  window.addEventListener('pageshow', hideIfLoaded)

  if (mv.loaded) {
    hide()
  } else {
    show()
  }

  return { hideIfLoaded }
}

function withDebugParamIfNeeded(href) {
  if (!href || !isUrlDebugEnabled()) return href
  try {
    const u = new URL(href, window.location.href)
    u.searchParams.set('debug', '')
    return u.href
  } catch {
    return href
  }
}

/** Full-screen loading overlay when navigating home from a preview page. */
function setupHomeNavigation() {
  const style = document.createElement('style')
  style.textContent = `
    .mv-nav-overlay {
      position: fixed;
      inset: 0;
      z-index: 999;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 14px;
      background: rgba(6, 8, 14, 0.68);
      opacity: 0;
      pointer-events: none;
      transition: opacity 140ms ease;
    }
    .mv-nav-overlay.is-visible {
      opacity: 1;
      pointer-events: auto;
    }
    .mv-nav-overlay__spinner {
      width: clamp(58px, 14vw, 76px);
      height: clamp(58px, 14vw, 76px);
      border-radius: 50%;
      border: 4px solid rgba(255, 255, 255, 0.3);
      border-top-color: #fff;
      animation: mv-nav-spin 0.9s linear infinite;
    }
    .mv-nav-overlay__text {
      margin: 0;
      font-family: "Quilt Sans", sans-serif;
      font-size: clamp(16px, 3.8vw, 22px);
      font-weight: 300;
      color: #fff;
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    }
    @keyframes mv-nav-spin {
      to { transform: rotate(360deg); }
    }
  `
  document.head.appendChild(style)

  const overlay = document.createElement('div')
  overlay.className = 'mv-nav-overlay'
  overlay.setAttribute('aria-hidden', 'true')
  overlay.innerHTML = `
    <div class="mv-nav-overlay__spinner" role="status" aria-label="Loading"></div>
  `
  document.body.appendChild(overlay)

  let isNavigating = false

  function resetNavigationUI() {
    isNavigating = false
    overlay.classList.remove('is-visible')
    overlay.setAttribute('aria-hidden', 'true')
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetNavigationUI()
  })

  document.querySelectorAll('a.back[href]').forEach((link) => {
    link.addEventListener('click', (event) => {
      if (isNavigating) {
        event.preventDefault()
        return
      }

      const href = link.getAttribute('href')
      if (!href) return

      event.preventDefault()
      isNavigating = true
      overlay.classList.add('is-visible')
      overlay.setAttribute('aria-hidden', 'false')

      window.requestAnimationFrame(() => {
        window.location.assign(withDebugParamIfNeeded(href))
      })
    })
  })
}

function getARPlacementHint() {
  const path = window.location.pathname.toLowerCase()

  if (path.includes('idu')) {
    return '*When viewing in AR, aim phone camera<br>at the wall for automatic placement.'
  }

  if (path.includes('odu')) {
    return '*When viewing in AR, aim phone camera<br>where the floor meets the wall for automatic placement.'
  }

  if (path.includes('dial')) {
    return '*When viewing in AR, aim phone camera<br>at the wall or a tabletop for automatic placement.'
  }

  return ''
}

function syncARRelatedOverlays() {
  const arBtn = document.getElementById('ar-btn')
  const overlays = document.querySelectorAll('.swatch-bar, .placement')

  overlays.forEach((overlay) => {
    overlay.style.zIndex = '21'

    if (arBtn) {
      const rect = arBtn.getBoundingClientRect()
      const gap = 20
      const desiredBottom = Math.max(24, window.innerHeight - (rect.top - gap))
      overlay.style.bottom = `${desiredBottom}px`
    } else {
      overlay.style.bottom = 'calc(24px + env(safe-area-inset-bottom))'
    }
  })
}

function setupARButton(mv, { hideIfLoaded } = {}) {
  if (!isMobile) return

  const wrapper = document.createElement('div')
  wrapper.style.position = 'fixed'
  wrapper.style.bottom = 'calc(12px + env(safe-area-inset-bottom))'
  wrapper.style.left = '50%'
  wrapper.style.transform = 'translateX(-50%)'
  wrapper.style.zIndex = '20'
  wrapper.style.display = 'flex'
  wrapper.style.flexDirection = 'column'
  wrapper.style.alignItems = 'center'
  wrapper.style.gap = '6px'
  wrapper.style.pointerEvents = 'none'

  const btn = document.createElement('button')
  btn.id = 'ar-btn'
  btn.textContent = 'View in AR*'
  Object.assign(btn.style, {
    border: 'none',
    borderRadius: '22px',
    padding: '12px 28px',
    background: '#fff',
    color: '#0b0b10',
    fontSize: '15px',
    fontWeight: '600',
    fontFamily: 'system-ui, sans-serif',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
    pointerEvents: 'auto',
  })

  const hint = document.createElement('p')
  hint.innerHTML = getARPlacementHint()
  hint.setAttribute('aria-live', 'polite')
  Object.assign(hint.style, {
    margin: '0',
    width: 'min(90vw, 320px)',
    color: 'rgba(255, 255, 255, 0.92)',
    fontSize: '11px',
    lineHeight: '1.3',
    fontWeight: '400',
    fontFamily: 'system-ui, sans-serif',
    textAlign: 'center',
    textShadow: '0 1px 8px rgba(0, 0, 0, 0.5)',
    whiteSpace: 'normal',
  })

  wrapper.appendChild(btn)
  wrapper.appendChild(hint)
  document.body.appendChild(wrapper)

  let busy = false

  function setIdle() {
    busy = false
    btn.textContent = 'View in AR*'
    btn.disabled = false
  }

  function setPreparing() {
    busy = true
    btn.textContent = 'Starting…'
    btn.disabled = true
  }

  function showError(message) {
    btn.textContent = message
    btn.disabled = true
    setTimeout(setIdle, 3000)
  }

  async function launchIOSQuickLook() {
    const blobUrl = await mv.prepareUSDZ()
    const anchor = document.createElement('a')
    anchor.rel = 'ar'
    anchor.href = blobUrl
    anchor.appendChild(document.createElement('img'))
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(blobUrl)
    await waitForReturnFromAR()
  }

  function launchAndroidAR() {
    return new Promise((resolve, reject) => {
      let started = false
      let settled = false

      const settle = (fn) => {
        if (settled) return
        settled = true
        cleanup()
        fn()
      }

      const onStatus = (event) => {
        const { status } = event.detail
        if (status === 'session-started' || status === 'object-placed') started = true
        if (status === 'not-presenting' && started) settle(() => resolve())
        if (status === 'failed') settle(() => reject(new Error('AR failed')))
      }

      const onVisibility = () => {
        if (document.hidden) started = true
        else if (started) settle(() => resolve())
      }

      const cleanup = () => {
        mv.removeEventListener('ar-status', onStatus)
        document.removeEventListener('visibilitychange', onVisibility)
        window.removeEventListener('pageshow', onVisibility)
        clearTimeout(safetyTimeout)
      }

      const safetyTimeout = setTimeout(() => settle(() => resolve()), 10 * 60 * 1000)

      mv.addEventListener('ar-status', onStatus)
      document.addEventListener('visibilitychange', onVisibility)
      window.addEventListener('pageshow', onVisibility)
      mv.activateAR()
    })
  }

  btn.addEventListener('click', async () => {
    if (busy) return

    releasePreviewCameras()
    setPreparing()
    await waitForPaint()

    try {
      if (isIOS) {
        await launchIOSQuickLook()
      } else {
        await launchAndroidAR()
      }
      setIdle()
      hideIfLoaded?.()
    } catch (err) {
      console.error('[ar] launch failed:', err)
      showError('AR unavailable')
      hideIfLoaded?.()
    }
  })
}

document.addEventListener('DOMContentLoaded', () => {
  const mv = document.querySelector('model-viewer')
  if (!mv) return

  // Replace the native AR button with nothing so we own the whole UI.
  const hiddenSlot = document.createElement('button')
  hiddenSlot.setAttribute('slot', 'ar-button')
  hiddenSlot.style.display = 'none'
  mv.appendChild(hiddenSlot)

  const loading = setupModelLoading(mv)
  setupARButton(mv, loading)
  syncARRelatedOverlays()
  setupHomeNavigation()
})

window.addEventListener('resize', syncARRelatedOverlays)
window.addEventListener('orientationchange', syncARRelatedOverlays)

// Carry ?debug on plain link navigation (non-JS fallback).
if (isUrlDebugEnabled()) {
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href')
      if (!href || href.startsWith('http') || href.startsWith('#')) return
      try {
        const u = new URL(href, window.location.href)
        u.searchParams.set('debug', '')
        link.setAttribute('href', u.href)
      } catch { /* ignore */ }
    })
  })
}
